import { useEffect, useRef, useState } from "react";
import { api, humanErrorMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, PackageCheck, Rocket, Upload } from "lucide-react";

const fmtMb = (b) => `${((b || 0) / 1024 / 1024).toFixed(1)} MB`;

// Iter 116 — slot "release" (só no preview): guarda o APK de PRODUÇÃO sem substituir o APK de testes.
// Em produção, "Copiar do preview" passa a trazer este arquivo; ou baixe e faça upload manual lá.
export const ReleaseSlotCard = ({ backendUrl }) => {
  const [info, setInfo] = useState(null);
  const [version, setVersion] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/mobile-app/release/info").then((r) => setInfo(r.data)).catch(() => setInfo({ available: false }));
  useEffect(() => { load(); }, []);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Selecione o .apk gerado com o perfil production");
    if (!f.name.endsWith(".apk")) return toast.error("Só aceita .apk");
    const fd = new FormData();
    fd.append("file", f); fd.append("version", version || "1.0.0"); fd.append("slot", "release");
    setUploading(true);
    try {
      const r = await api.post("/mobile-app/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`APK de produção v${r.data.version} guardado no slot release (${fmtMb(r.data.size_bytes)})`);
      fileRef.current.value = ""; load();
    } catch (e) { toast.error(humanErrorMsg(e, "Falha ao guardar o APK de produção")); }
    finally { setUploading(false); }
  };

  const downloadUrl = `${backendUrl}/api/mobile-app/release/download`;
  return (
    <Card className="border-emerald-200" data-testid="apk-release-card">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Rocket className="w-5 h-5 text-emerald-700" />
          <div className="font-bold">APK de produção (release para usegestao.app.br)</div>
        </div>
        <p className="text-xs text-muted-foreground">
          Este slot não mexe no APK que os entregadores baixam <b>aqui no preview</b>. Ele guarda o build <b>production</b> para o painel de produção
          trazer com <b>“Copiar do preview”</b> (após o deploy) — ou baixe o arquivo abaixo e faça o upload manual em <code className="bg-slate-100 px-1 rounded">usegestao.app.br/admin/mobile-app</code>.
        </p>
        <div className="text-sm" data-testid="apk-release-info">
          {info === null ? "Consultando…" : info.available ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="inline-flex items-center gap-1 text-emerald-700 font-bold"><PackageCheck className="w-4 h-4" />v{info.version}</span>
              <span className="text-muted-foreground">{fmtMb(info.size_bytes)}</span>
              <span className="text-muted-foreground">aponta para <code className="text-[11px]">{(info.api_url || "—").replace(/^https?:\/\//, "")}</code></span>
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-800">{info.app_variant || "production"}</span>
              <span className="text-muted-foreground">{info.updated_at ? new Date(info.updated_at).toLocaleString("pt-BR") : ""}</span>
              <a href={downloadUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-700 font-bold hover:underline" data-testid="apk-release-download-link">
                <Download className="w-4 h-4" />Baixar APK de produção
              </a>
            </div>
          ) : <span className="text-amber-800" data-testid="apk-release-empty">Nenhum APK de produção guardado ainda.</span>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-xs font-bold uppercase text-slate-600">Versão</label>
            <input type="text" value={version} onChange={(e) => setVersion(e.target.value)} className="w-full border-2 rounded p-2 text-sm mt-1" placeholder="2.3.0" data-testid="apk-release-version-input" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-bold uppercase text-slate-600">Arquivo APK (perfil production)</label>
            <input ref={fileRef} type="file" accept=".apk" className="w-full border-2 rounded p-2 text-sm mt-1" data-testid="apk-release-file-input" />
          </div>
        </div>
        <Button onClick={upload} disabled={uploading} className="bg-emerald-700 hover:bg-emerald-800" data-testid="apk-release-upload-btn">
          <Upload className="w-4 h-4 mr-2" />{uploading ? "Enviando…" : "Guardar APK de produção"}
        </Button>
      </CardContent>
    </Card>
  );
};
